import axios from "../axiosApi";
import {
    FETCH_INSTALLMENTS, RECEIVE_INSTALLMENTS, RECEIVE_INSTALLMENT_CONTEXT,
    SAVE_INSTALLMENT, SAVE_INSTALLMENT_SUCCESS,
    DELETE_INSTALLMENT, DELETE_INSTALLMENT_SUCCESS,
    FETCH_INSTALLMENTS_SUMMARY, RECEIVE_INSTALLMENTS_SUMMARY,
    FETCH_INSTALLMENTS_DETAILED, RECEIVE_INSTALLMENTS_DETAILED
} from "../actionTypes";

/** Format datetime to MySQL-compatible YYYY-MM-DD HH:mm:ss (current São Paulo time) */
const toMySQLDatetime = (date?: Date | string | null): string => {
    const d = date ? new Date(date) : new Date();
    return d.toLocaleString('sv-SE', { timeZone: 'America/Sao_Paulo' }).replace('T', ' ');
};

/**
 * Format date-only field for MySQL GMT storage.
 * MySQL stores in GMT, app works in GMT-3.
 * To preserve the correct day, date-only values must be stored as YYYY-MM-DD 03:00:00
 * so that GMT-3 conversion yields YYYY-MM-DD 00:00:00 (same day).
 */
const toMySQLDateOnly = (date?: Date | string | null): string => {
    if (!date) return '';
    // Extract YYYY-MM-DD from any date format
    const d = typeof date === 'string' ? date : date.toISOString();
    const dayPart = d.substring(0, 10); // YYYY-MM-DD
    return `${dayPart} 03:00:00`;
};

export const getInstallmentsByEnrollment = (enrollmentId: number) => {
    return async (dispatch: any) => {
        dispatch({ type: FETCH_INSTALLMENTS });
        try {
            const filter = JSON.stringify({
                "where": { "Enrollment_id": enrollmentId },
                "order": "NumberInEnrollment ASC"
            });
            const response = await axios.get(`/Installments?filter=${filter}`);
            dispatch({ type: RECEIVE_INSTALLMENTS, payload: response.data });
        } catch (e) {
            console.error("getInstallmentsByEnrollment ERROR", e);
            dispatch({ type: RECEIVE_INSTALLMENTS, payload: [] });
        }
    }
}

/**
 * Fetches enrollment + student + installments by PaymentCard number.
 * Mirrors old flow: Enrollment by PaymentCard → Person by Person_id → Installments by Enrollment_id
 */
export const getInstallmentsByPaymentCard = (paymentCardNumber: string) => {
    return async (dispatch: any) => {
        dispatch({ type: FETCH_INSTALLMENTS });
        try {
            // 1) Fetch Enrollment by PaymentCard
            const enrollFilter = JSON.stringify({
                where: { PaymentCard: paymentCardNumber },
                include: ["enrollmentInLevel"]
            });
            const enrollRes = await axios.get(`/Enrollments?filter=${enrollFilter}`);
            const enrollments = enrollRes.data;

            if (!enrollments || enrollments.length === 0) {
                dispatch({ type: RECEIVE_INSTALLMENTS, payload: [] });
                dispatch({ type: RECEIVE_INSTALLMENT_CONTEXT, payload: { enrollment: null, student: null } });
                return;
            }

            const enrollment = enrollments[0];
            dispatch({ type: RECEIVE_INSTALLMENT_CONTEXT, payload: { enrollment, student: null } });

            // 2) Fetch Person (student) by Person_id from enrollment
            const personId = enrollment.enrollmentInLevel
                ? enrollment.enrollmentInLevel.Person_id
                : enrollment.Person_id;

            if (personId) {
                try {
                    const personFilter = JSON.stringify({ where: { id: personId } });
                    const personRes = await axios.get(`/Persons?filter=${personFilter}`);
                    const student = personRes.data && personRes.data.length > 0 ? personRes.data[0] : null;
                    dispatch({ type: RECEIVE_INSTALLMENT_CONTEXT, payload: { enrollment, student } });
                } catch (e) {
                    console.error("getStudentByPaymentCard Person fetch error", e);
                }
            }

            // 3) Fetch Installments by Enrollment_id
            const instFilter = JSON.stringify({
                where: { Enrollment_id: enrollment.id },
                order: "NumberInEnrollment ASC"
            });
            const instRes = await axios.get(`/Installments?filter=${instFilter}`);
            dispatch({ type: RECEIVE_INSTALLMENTS, payload: instRes.data });

        } catch (e) {
            console.error("getInstallmentsByPaymentCard ERROR", e);
            dispatch({ type: RECEIVE_INSTALLMENTS, payload: [] });
            dispatch({ type: RECEIVE_INSTALLMENT_CONTEXT, payload: { enrollment: null, student: null } });
        }
    }
}

/**
 * Full payment flow matching old receiveInstallmentPaymentApiCall:
 * - Sets PayDate in São Paulo timezone
 * - Adjusts Amount for partial payments
 * - Creates new installment for remaining balance
 * - Creates CashTransaction log entry
 */
export const receiveInstallmentPayment = (installmentData: any, id: number) => {
    return async (dispatch: any) => {
        dispatch({ type: SAVE_INSTALLMENT });
        try {
            // PayDate: MySQL-compatible format in São Paulo timezone
            const pd = toMySQLDatetime(installmentData.PayDate || undefined);

            // Calculate if partial payment
            let am = installmentData.Amount;
            if (installmentData.TotalPayment < installmentData.Total) {
                am = installmentData.TotalPayment + (installmentData.Discount || 0) - (installmentData.Fee || 0) - (installmentData.Interest || 0);
            }

            const installmentUpdate = {
                id: installmentData.id,
                NumberInEnrollment: installmentData.NumberInEnrollment,
                DueDate: toMySQLDateOnly(installmentData.DueDate),
                Amount: am,
                PayDate: pd,
                Fee: installmentData.Fee || 0,
                Interest: installmentData.Interest || 0,
                Discount: installmentData.Discount || 0,
                TotalPayment: installmentData.TotalPayment,
                Enrollment_id: installmentData.Enrollment_id,
                Comment: installmentData.Comment || ''
            };

            // 1) Update the installment
            const response = await axios.put(`/Installments/${id}`, installmentUpdate);
            dispatch({ type: SAVE_INSTALLMENT_SUCCESS, payload: response.data });

            // 2) If partial payment, create new installment for remaining balance
            if (installmentData.TotalPayment < installmentData.Total) {
                const remainingAmount = installmentData.Total - installmentData.TotalPayment;
                const newNumber = parseFloat(installmentData.NumberInEnrollment) + 0.1;

                const newInstallment = {
                    ...installmentUpdate,
                    PayDate: null,
                    Fee: null,
                    Interest: null,
                    TotalPayment: null,
                    Discount: null,
                    id: null,
                    Amount: remainingAmount,
                    NumberInEnrollment: newNumber,
                    Comment: null
                };

                try {
                    await axios.put('/Installments/', newInstallment);
                } catch (err) {
                    console.error("Error creating remaining installment", err);
                }
            }

            // 3) Create CashTransaction log
            const transaction = {
                Installments_id: installmentData.id,
                PaymentTypes_id: parseInt(installmentData.PaymentType),
                Person_id: installmentData.Person_id,
                CashCycle_id: installmentData.CashCycle_id,
                Amount: installmentData.TotalPayment,
                Description: 'MENSALIDADE ' + installmentData.PaymentCard + '/' + installmentData.NumberInEnrollment,
                isCredit: true,
                Date: pd,
                Comment: null
            };

            try {
                await axios.post('/CashTransactions', transaction);
            } catch (err) {
                console.error("Error creating CashTransaction", err);
            }

        } catch (e) {
            console.error("receiveInstallmentPayment ERROR", e);
            dispatch({ type: SAVE_INSTALLMENT_SUCCESS, payload: { error: true } });
        }
    }
}

/**
 * PATCH installment Comment and DueDate only
 */
export const setInstallmentComment = (data: { id: number; Comment: string; DueDate: string }) => {
    return async (dispatch: any) => {
        dispatch({ type: SAVE_INSTALLMENT });
        try {
            const payload = {
                id: data.id,
                Comment: data.Comment,
                DueDate: toMySQLDateOnly(data.DueDate)
            };
            const response = await axios.patch(`/Installments/${data.id}`, payload);
            dispatch({ type: SAVE_INSTALLMENT_SUCCESS, payload: response.data });
        } catch (e) {
            console.error("setInstallmentComment ERROR", e);
            dispatch({ type: SAVE_INSTALLMENT_SUCCESS, payload: { error: true } });
        }
    }
}

export const saveInstallment = (data: any) => {
    return async (dispatch: any) => {
        dispatch({ type: SAVE_INSTALLMENT });
        try {
            const id = data.id;
            const payload = { ...data };
            delete payload.id;
            const response = await axios.patch(`/Installments/${id}`, payload);
            dispatch({ type: SAVE_INSTALLMENT_SUCCESS, payload: response.data });
        } catch (e) {
            console.error("saveInstallment ERROR", e);
            dispatch({ type: SAVE_INSTALLMENT_SUCCESS, payload: { error: true } });
        }
    }
}

export const deleteInstallment = (id: number) => {
    return async (dispatch: any) => {
        dispatch({ type: DELETE_INSTALLMENT });
        try {
            await axios.delete(`/Installments/${id}`);
            dispatch({ type: DELETE_INSTALLMENT_SUCCESS });
        } catch (e) {
            console.error("deleteInstallment ERROR", e);
        }
    }
}

export const getSumOpenInstallments = () => {
    return async (dispatch: any) => {
        dispatch({ type: FETCH_INSTALLMENTS_SUMMARY });
        try {
            const threeYearsAgo = new Date();
            threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
            const filter = JSON.stringify({
                where: {
                    DueDate: { gte: threeYearsAgo.toISOString() }
                },
                fields: { id: true, Amount: true, AmountPaid: true, TotalPayment: true }
            });
            const response = await axios.get(`/Installments?filter=${filter}`);
            const items = Array.isArray(response.data) ? response.data : [];
            const totalOpen = items.reduce((acc: number, item: any) => {
                const paid = item.AmountPaid || item.TotalPayment || 0;
                const saldo = (item.Amount || 0) - paid;
                return acc + Math.max(saldo, 0);
            }, 0);
            dispatch({ type: RECEIVE_INSTALLMENTS_SUMMARY, payload: { total: totalOpen, count: items.length } });
        } catch (e) {
            console.error("getSumOpenInstallments ERROR", e);
            dispatch({ type: RECEIVE_INSTALLMENTS_SUMMARY, payload: null });
        }
    }
}

export const getSumOpenInstallmentsDetailed = () => {
    return async (dispatch: any) => {
        dispatch({ type: FETCH_INSTALLMENTS_DETAILED });
        try {
            const threeYearsAgo = new Date();
            threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
            const filter = JSON.stringify({
                where: {
                    DueDate: { gte: threeYearsAgo.toISOString() }
                },
                order: "DueDate ASC"
            });
            const response = await axios.get(`/Installments?filter=${filter}`);
            const items = Array.isArray(response.data) ? response.data : [];
            const normalized = items.map((item: any) => ({
                ...item,
                AmountPaid: item.AmountPaid || item.TotalPayment || 0,
                DataPagto: item.DataPagto || item.PayDate || null,
                PaymentType: item.PaymentTypes_id || item.PaymentType || 0,
                PersonName: item.PersonName || item.Name || '',
                MobileNumber: item.MobileNumber || item.Phone || '',
            }));
            dispatch({ type: RECEIVE_INSTALLMENTS_DETAILED, payload: normalized });
        } catch (e) {
            console.error("getSumOpenInstallmentsDetailed ERROR", e);
            dispatch({ type: RECEIVE_INSTALLMENTS_DETAILED, payload: [] });
        }
    }
}
